import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  TrendingUp,
  Briefcase,
  MapPin,
  Search,
  Zap,
  ChevronRight,
  Loader2,
  Award,
  AlertTriangle,
  Users,
  Star,
  BarChart3,
  Hash
} from "lucide-react";

const TrendingJobs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trendingJobs, setTrendingJobs] = useState([]);
  const [searches, setSearches] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [jobsRes, searchRes, indRes] = await Promise.all([
        fetch(`${API}/trending/jobs?limit=12`),
        fetch(`${API}/trending/searches`),
        fetch(`${API}/trending/industries`),
      ]);
      if (jobsRes.ok) setTrendingJobs(await jobsRes.json());
      if (searchRes.ok) setSearches(await searchRes.json());
      if (indRes.ok) setIndustries(await indRes.json());
    } catch {
      toast.error("Failed to load trending data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" data-testid="trending-title">
              <TrendingUp className="w-7 h-7 text-crimson" />
              Trending Jobs & Searches
            </h1>
            <p className="text-gray-400 mt-1 text-sm">Discover what's hot in the job market right now</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-crimson" />
            </div>
          ) : (
            <>
              {/* Trending Searches Row */}
              {searches && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Popular Titles */}
                  <div className="bg-obsidian-light border border-[#222] rounded-lg p-5">
                    <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                      <Briefcase className="w-4 h-4 text-crimson" /> Popular Titles
                    </h3>
                    <div className="space-y-2">
                      {searches.trending_titles?.slice(0, 8).map((t, i) => (
                        <Link
                          key={i}
                          to={`/jobs?job_title=${encodeURIComponent(t.title)}`}
                          className="flex items-center justify-between text-sm hover:text-crimson transition-colors"
                          data-testid={`title-${i}`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 w-5">{i + 1}.</span>
                            {t.title}
                          </span>
                          <span className="text-xs text-gray-500">{t.count} jobs</span>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Popular Skills */}
                  <div className="bg-obsidian-light border border-[#222] rounded-lg p-5">
                    <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                      <Zap className="w-4 h-4 text-amber-400" /> In-Demand Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {searches.trending_skills?.slice(0, 12).map((s, i) => (
                        <Link
                          key={i}
                          to={`/jobs?skills=${encodeURIComponent(s.skill)}`}
                          className="px-3 py-1.5 bg-obsidian border border-[#333] rounded text-xs text-gray-300 hover:border-crimson hover:text-crimson transition-colors"
                          data-testid={`skill-${i}`}
                        >
                          {s.skill} <span className="text-gray-600 ml-1">({s.count})</span>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Popular Locations */}
                  <div className="bg-obsidian-light border border-[#222] rounded-lg p-5">
                    <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                      <MapPin className="w-4 h-4 text-emerald-400" /> Top Locations
                    </h3>
                    <div className="space-y-2">
                      {searches.trending_locations?.slice(0, 8).map((l, i) => (
                        <Link
                          key={i}
                          to={`/jobs?location=${encodeURIComponent(l.location)}`}
                          className="flex items-center justify-between text-sm hover:text-crimson transition-colors"
                          data-testid={`location-${i}`}
                        >
                          <span className="flex items-center gap-2">
                            <MapPin className="w-3 h-3 text-gray-600" />
                            {l.location}
                          </span>
                          <span className="text-xs text-gray-500">{l.count}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Industries */}
              {industries.length > 0 && (
                <div className="bg-obsidian-light border border-[#222] rounded-lg p-5">
                  <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                    <BarChart3 className="w-4 h-4 text-crimson" /> Top Industries
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {industries.map((ind, i) => (
                      <Link
                        key={i}
                        to={`/jobs?industry=${encodeURIComponent(ind.industry)}`}
                        className="flex items-center gap-2 px-3 py-1.5 bg-obsidian border border-[#333] rounded text-xs hover:border-crimson transition-colors"
                        data-testid={`industry-${i}`}
                      >
                        <Hash className="w-3 h-3 text-gray-600" />
                        <span className="text-gray-300">{ind.industry}</span>
                        <span className="text-gray-600">{ind.job_count}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Trending Job Cards */}
              <div>
                <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-crimson" /> Trending Jobs
                </h2>
                {trendingJobs.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Briefcase className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No trending jobs at the moment</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {trendingJobs.map((job, idx) => (
                      <motion.div
                        key={job.job_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.04 }}
                      >
                        <Link
                          to={`/jobs/${job.job_id}`}
                          className="block bg-obsidian-light border border-[#222] hover:border-[#444] rounded-lg p-4 transition-colors group"
                          data-testid={`trending-job-${idx}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-sm group-hover:text-crimson transition-colors line-clamp-2">
                              {job.title}
                            </h3>
                            {job.is_urgent && (
                              <span className="flex-shrink-0 px-1.5 py-0.5 bg-amber-500/20 border border-amber-500/30 rounded text-xs text-amber-400 font-bold">
                                Urgent
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{job.company_name}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            {job.location && (
                              <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{job.location}</span>
                            )}
                            {job.employment_type && <span>{job.employment_type}</span>}
                            {job.job_function && <span className="text-gray-600">{job.job_function}</span>}
                          </div>
                          {job.application_count > 0 && (
                            <div className="mt-2 flex items-center gap-1 text-xs text-crimson">
                              <TrendingUp className="w-3 h-3" />
                              {job.application_count} applications
                            </div>
                          )}
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default TrendingJobs;
